import React, { useState } from "react"
import ReactPlayer from "react-player"
import Image from "gatsby-image"
import styled, { css } from "styled-components"
import { useWindowSize } from "react-use"

import PlayPauseSvg from "./../images/graphics/play-pause.svg"

const Wrapper = styled.div`
  position: relative;

  ${props => props.theme.above.desktop`
        width: 100%;
        height: 100%;

        aspect-ratio: 1/1;
    `}
`

const Overlay = styled.button`
  ${props => props.theme.styles.element.fill};

  cursor: pointer;
`

const StyledVideo = styled.div`
  ${props => props.theme.below.desktop`
        position: relative;

        padding-top: 56.25%;
    `}

  ${props => props.theme.above.desktop`
        position: absolute;

        top: 50%;
        left: 50%;

        transform: translate(-50%, -50%);

        width: calc(${props.theme.desktopVW(720)} * 2);
        height: 100%;
    `}
`

const Player = styled(ReactPlayer)`
  ${props => props.theme.below.desktop`
        position: absolute;
        
        top: 0;
        left: 0;

        width: 100%;
    `}
`

const ImageStyles = css`
  ${props => props.theme.styles.element.fill};

  pointer-events: none;

  opacity: ${props => (props.visible == "true" ? 1 : 0)};
`

const FluidImage = styled(Image)`
  ${ImageStyles}
`

const StaticImage = styled.img`
  ${ImageStyles}
`

const ErrorMessage = styled.span`
  position: absolute;
  z-index: 2;
  right: ${props => props.theme.sizes.mobile};
  bottom: ${props => props.theme.sizes.mobile};
  left: ${props => props.theme.sizes.mobile};

  font-family: ${props => props.theme.fontFamilies.plainLight};
  font-size: ${props => props.theme.fontSizes.mobile.s};
  text-align: center;

  ${props => props.theme.above.desktop`
    right: ${props.theme.sizes.desktop};
    bottom: ${props.theme.sizes.desktop};
    left: ${props.theme.sizes.desktop};

    font-size: ${props.theme.fontSizes.desktop.s};
  `}
`

const Controls = styled.div`
  position: absolute;
  overflow: hidden;

  bottom: calc(${props => props.theme.sizes.mobile} / 2);
  left: 0;

  width: 100%;
  height: 100%;

  padding: 0 calc(${props => props.theme.sizes.mobile} / 2);

  ${props => (props.videoPlaying == "true" ? `opacity: 0;` : `opacity: 1;`)}

  transition: all 0.25s ease-out;

  ${props => props.theme.above.desktop`
    bottom: ${props.theme.sizes.desktop};

    padding: 0 ${props.theme.sizes.desktop};

    ${props =>
      props.videoPlaying == "true"
        ? `opacity: 0; &:hover { opacity: 1 }`
        : `opacity: 1;`}
  
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
    }
  `}
`

const PlayPause = styled.button`
  cursor: pointer;

  width: 100%;
  height: 100%;
`

const Icon = styled(PlayPauseSvg)`
  width: ${props => props.theme.mobileVW(15)};
  height: ${props => props.theme.mobileVW(20)};

  rect,
  polygon {
    fill: ${props => props.theme.colors.light};
  }

  ${props =>
    props.playing == "true"
      ? `
        .pause {
            display: block;
        }

        .play {
            display: none;
        }
    `
      : `
        .pause {
            display: none;
        }

        .play {
            display: block;
        }
    `}

  ${props => props.theme.above.desktop`
        width: ${props.theme.desktopVW(20)};
        height: ${props.theme.desktopVW(15)};
    `}
`

const normalizeVideoUrl = url => {
  if (!url || !url.includes("vimeo.com")) return url

  return url
    .replace(/([?&])share=copy(&|$)/, (match, separator, suffix) =>
      suffix ? separator : ""
    )
    .replace(/[?&]$/, "")
}

const getVideoProvider = url =>
  url && url.includes("vimeo.com") ? "vimeo" : ""

const Video = ({
  className,
  url,
  inView,
  placeholder,
  title,
  inline,
  trackDataLayer,
}) => {
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [videoStarted, setVideoStarted] = useState(false)
  const [videoError, setVideoError] = useState(false)

  const playerUrl = normalizeVideoUrl(url)

  const pushVideoEvent = event => {
    if (!trackDataLayer || typeof window === "undefined") return

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event,
      video_details: {
        video_title: title || "",
        video_url: playerUrl || "",
        video_provider: getVideoProvider(playerUrl),
      },
    })
  }

  const toggleVideoPlaying = () => {
    setVideoError(false)
    setVideoPlaying(!videoPlaying)
  }

  const handleVideoStart = () => {
    setVideoStarted(true)
    pushVideoEvent("video_start")
  }

  const handleVideoEnd = () => {
    setVideoPlaying(false)
    pushVideoEvent("video_complete")
  }

  const handleVideoError = () => {
    setVideoPlaying(false)
    setVideoError(true)
  }

  return (
    <Wrapper className={`video-wrapper ${className || ""}`}>
      <StyledVideo className="video">
        <Player
          url={playerUrl}
          width="100%"
          height="100%"
          playing={videoPlaying}
          onStart={handleVideoStart}
          onEnded={handleVideoEnd}
          onError={handleVideoError}
          // muted={videoPlaying ? false : true}
          volume={videoPlaying ? 1 : 0}
        />
      </StyledVideo>
      {placeholder && placeholder.fluid && (
        <FluidImage
          fluid={placeholder.fluid}
          alt={placeholder.title || title || ""}
          visible={(!videoStarted).toString()}
        />
      )}
      {videoError && <ErrorMessage>Video currently unavailable</ErrorMessage>}
      <Overlay
        type="button"
        aria-label={`${videoPlaying ? "Pause" : "Play"} ${title || "video"}`}
        onClick={toggleVideoPlaying}
      />
      <Controls
        className="video-controls"
        videoPlaying={videoPlaying.toString()}
      >
        <PlayPause
          type="button"
          className="video-play"
          aria-label={`${videoPlaying ? "Pause" : "Play"} ${title || "video"}`}
          onClick={toggleVideoPlaying}
          // style={{ opacity: videoPlaying ? 0.25 : 1 }}
        >
          <Icon playing={videoPlaying.toString()} />
        </PlayPause>
      </Controls>
    </Wrapper>
  )
}

export default Video
